import { videoDecoder } from "./video";
import { Img, Layout, Rect, Video, type Node } from "@revideo/2d";
import { clipSeed, timelineOverlays, vary, type Clip, type Overlay } from "@hudhud/core";
import { CORNER_WIDTH, FOLLOW_SHRINK, HEIGHT, WIDTH, cardPlacement, enterMotion, faceOnCanvas, followPlacement, frameToMs, motionValues, msToFrame, overlayFaceY, overlayFrame } from "@hudhud/reel/math";
import { coverSize, loadImage } from "./assets";
import { ellipseRect } from "./gradients";

/** The picture itself, cover-fitted into a `w`×`h` box: a still, or a B-roll video looping silently. */
function picture(src: string, video: boolean, w: number, h: number, scale: () => number, txPct: () => number): Node {
  if (video) {
    return new Video({ src, decoder: videoDecoder.current, loop: true, play: true, volume: 0, width: w, height: h, scale, x: () => (txPct() / 100) * w });
  }
  const img = loadImage(src);
  const size = () => coverSize(img.naturalWidth, img.naturalHeight, w, h);
  return new Img({ src, width: () => size()[0], height: () => size()[1], scale, x: () => (txPct() / 100) * size()[0] });
}

interface PictureInput { overlay: Overlay; src: string; video: boolean; frames: number; accent: string; faceY: number | null; capTop: number; seed: number; frame: () => number; face: () => { x: number; y: number } | null }

/** One reference image, shown as a card / full-bleed / corner polaroid with a soft fade and a slow push-in. Mirrors OverlayImage. */
function pictureNode(o: PictureInput): Node {
  const { overlay } = o;
  const st = () => overlayFrame(overlay, o.frame(), o.frames);
  const enter = () => enterMotion(overlay.enter, st().enterT);
  const move = () => motionValues(overlay.motion, st().moveP);
  const nudge = () => [overlay.offsetX + (enter().tx ?? 0), overlay.offsetY + (enter().ty ?? 0)] as [number, number];
  const enterScale = () => enter().scale ?? 1;
  // the slow motion while it shows: the chosen motion, or the plain push that a still card gets
  const pictureScale = () => overlay.scale * (move().scale ?? (overlay.style === "full" ? 1 : st().drift));
  const pictureShift = () => move().txPct ?? 0;

  if (overlay.style === "full") {
    return new Layout({ width: WIDTH, height: HEIGHT, opacity: () => st().inOut, children: [
      new Rect({ width: WIDTH, height: HEIGHT, clip: true, position: nudge, scale: enterScale, children: [
        picture(o.src, o.video, WIDTH, HEIGHT, pictureScale, pictureShift),
        ellipseRect(WIDTH, HEIGHT, 0.5, 0.45, [{ offset: 0.5, color: "rgba(0,0,0,0)" }, { offset: 1, color: "rgba(0,0,0,0.55)" }]),
      ] }),
    ] });
  }
  if (overlay.style === "corner") {
    const w = CORNER_WIDTH(overlay.scale);
    const inner = w - 24, innerH = inner * 5 / 4;
    return new Layout({ width: WIDTH, height: HEIGHT, opacity: () => st().inOut, children: [
      new Rect({
        layout: true, direction: "column", padding: 12, width: w, fill: "#F7F5EF", radius: 10, shadowColor: "rgba(0,0,0,0.5)", shadowBlur: 60, shadowOffset: [0, 24],
        offset: [1, -1], x: WIDTH / 2 - 56, y: 200 - HEIGHT / 2,
        position: () => [WIDTH / 2 - 56 + nudge()[0], 200 - HEIGHT / 2 + nudge()[1]],
        rotation: -6 + 4 * vary(o.seed, 11), scale: () => enterScale() * (0.97 + 0.03 * st().inOut),
        children: [new Rect({ width: inner, height: innerH, clip: true, radius: 4, children: [picture(o.src, o.video, inner, innerH, pictureScale, pictureShift)] })],
      }),
    ] });
  }
  // card: a framed picture in whichever band is free of the speaker's face and the captions, or beside the face when it follows the speaker
  const box = cardPlacement(o.faceY, o.capTop);
  const shrink = overlay.follow ? FOLLOW_SHRINK : 1;
  const w = box.width * overlay.scale * shrink, h = box.height * overlay.scale * shrink;
  const at = () => { const f = o.face(); return overlay.follow && f ? followPlacement(f, w, h) : { left: (WIDTH - w) / 2, top: box.top + (box.height - h) / 2 }; };
  const centre = () => [at().left + w / 2 - WIDTH / 2 + nudge()[0], at().top + h / 2 - HEIGHT / 2 + nudge()[1]] as [number, number];
  return new Layout({ width: WIDTH, height: HEIGHT, opacity: () => st().inOut, children: [
    new Layout({ position: centre, scale: () => enterScale() * (0.96 + 0.04 * st().inOut), children: [
      new Rect({ width: w + 8, height: h + 8, radius: 28, fill: o.accent, shadowColor: "rgba(0,0,0,0.55)", shadowBlur: 80, shadowOffset: [0, 30] }),
      new Rect({ width: w, height: h, radius: 24, clip: true, children: [picture(o.src, o.video, w, h, pictureScale, pictureShift)] }),
    ] }),
  ] });
}

/** All reference images, each on its own stretch of the speech timeline: one continuous run that crosses any cuts inside it. */
export function overlaysLayer(o: { clip: Clip; baseUrl?: string; frame: () => number; from: number }): Node | null {
  if (!o.baseUrl) return null;
  const { clip } = o;
  const items = timelineOverlays(clip).map(({ overlay, range: r }) => {
    const from = msToFrame(r.inMs);
    const frames = Math.max(1, msToFrame(r.outMs) - from);
    const local = () => o.frame() - o.from - from;
    const on = () => { const l = local(); return l >= 0 && l < frames; };
    const face = () => (overlay.follow ? faceOnCanvas(clip, r.inMs + frameToMs(local())) : null);
    const node = pictureNode({
      overlay, src: `${o.baseUrl}/${overlay.video ?? overlay.image}`, video: !!overlay.video, frames, accent: clip.hook.accentColor,
      faceY: overlayFaceY(clip, overlay), capTop: clip.style.yPos - 0.05, seed: clipSeed(clip), frame: local, face,
    });
    return new Layout({ width: WIDTH, height: HEIGHT, opacity: () => (on() ? 1 : 0), children: [node] });
  });
  return items.length ? new Layout({ width: WIDTH, height: HEIGHT, children: items }) : null;
}
